import { message } from 'antd';
import { useCallback } from 'react';
import { fetchAdminProductMaterialDetails } from '../../services';
import type { AdminProduct, UseAdminProductCatalogArgs } from '../interfaces';
import { createDraftPriceMap, createDraftStockMap } from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductMaterialDetailsArgs = Pick<
  UseAdminProductCatalogArgs,
  | 'loadingMaterialDetailsById'
  | 'products'
  | 'setBaseProducts'
  | 'setDraftPriceById'
  | 'setDraftStockById'
  | 'setLoadingMaterialDetailsById'
  | 'setProducts'
>;

const hasMaterialDetails = (material?: AdminProduct['materialPrices'][number]) =>
  Boolean(
    material &&
    (
      Array.isArray(material.sizes) ||
      Array.isArray(material.variantInventories) ||
      Array.isArray(material.productImages)
    )
  );

const mergeMaterialDetails = (
  productList: AdminProduct[],
  productId: string,
  materialDetails: AdminProduct['materialPrices'][number]
) =>
  productList.map((product) =>
    product.id !== productId
      ? product
      : {
        ...product,
        materialPrices: product.materialPrices.map((material) =>
          material.id === materialDetails.id ? { ...material, ...materialDetails } : material
        ),
      }
  );

export const useAdminProductMaterialDetails = ({
  loadingMaterialDetailsById,
  products,
  setBaseProducts,
  setDraftPriceById,
  setDraftStockById,
  setLoadingMaterialDetailsById,
  setProducts,
}: UseAdminProductMaterialDetailsArgs) => {
  const handleLoadMaterialDetails = useCallback(async (productId: string, materialPriceId: string) => {
    const product = products.find((item) => item.id === productId);
    const material = product?.materialPrices.find((item) => item.id === materialPriceId);

    if (!product || hasMaterialDetails(material) || loadingMaterialDetailsById[materialPriceId]) {
      return;
    }

    try {
      setLoadingMaterialDetailsById((prev) => ({ ...prev, [materialPriceId]: true }));
      const response = await fetchAdminProductMaterialDetails(productId, materialPriceId);
      const materialDetails = response.product?.materialPrices?.[0];

      if (!materialDetails) {
        throw new Error('Material details were not found.');
      }

      setBaseProducts((prev) => mergeMaterialDetails(prev, productId, materialDetails));
      setProducts((prev) => mergeMaterialDetails(prev, productId, materialDetails));
      setDraftStockById((prev) => ({
        ...prev,
        ...createDraftStockMap([{ ...product, materialPrices: [materialDetails] }]),
      }));
      setDraftPriceById((prev) => ({
        ...prev,
        ...createDraftPriceMap([{ ...product, materialPrices: [materialDetails] }]),
      }));
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to load material details.'));
    } finally {
      setLoadingMaterialDetailsById((prev) => ({ ...prev, [materialPriceId]: false }));
    }
  }, [
    loadingMaterialDetailsById,
    products,
    setBaseProducts,
    setDraftPriceById,
    setDraftStockById,
    setLoadingMaterialDetailsById,
    setProducts,
  ]);

  return { handleLoadMaterialDetails };
};
